import {BOUNDARY_ADJACENCY_EPSILON, ItemTransitionType, MAX_TRANSITION_DURATION, MIN_TRANSITION_DURATION} from '@PosterWhiteboard/page/transitions/page-transitions.types';
import type {ItemTransitionObject, PageTransitionsObject, TransitionBoundaryId, TransitionWindow} from '@PosterWhiteboard/page/transitions/page-transitions.types';
import {assertNever} from '@Utils/assert-never.util';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import type {Page} from '@PosterWhiteboard/page/page.class';

/** Extra playback lead-in/out around the transition window when previewing. */
export const TRANSITION_PREVIEW_PADDING_SECONDS = 0.3;

/** An item's on-page time span, as read from the live item or the redux mirror. */
export interface TransitionEndpointSpan {
  startTime: number;
  endTime: number;
}

/**
 * The span a transition renders across: centered on the boundary, half-duration clamped to
 * half of each adjacent background span (so a short middle background's in- and out-windows
 * can never overlap). Null when the pair isn't temporally adjacent — a mid-mutation state
 * that must not render. Single source of the clamp math, shared by PageTransitions
 * (live items) and the timeline's transition region (mirror objects).
 */
export const getTransitionWindowForSpans = (duration: number, fromSpan: TransitionEndpointSpan, toSpan: TransitionEndpointSpan): TransitionWindow | null => {
  const boundaryTime = toSpan.startTime;
  if (Math.abs(fromSpan.endTime - boundaryTime) > BOUNDARY_ADJACENCY_EPSILON) {
    return null;
  }
  const fromLength = fromSpan.endTime - fromSpan.startTime;
  const toLength = toSpan.endTime - toSpan.startTime;
  const halfDuration = Math.min(duration / 2, fromLength / 2, toLength / 2);
  if (halfDuration <= 0) {
    return null;
  }
  return {
    startTime: boundaryTime - halfDuration,
    endTime: boundaryTime + halfDuration,
    boundaryTime,
  };
};

/**
 * Resolves the boundary a background edits in the transition UI: the boundary AFTER it (its
 * out-transition). Per the TransitionBoundaryId convention the id is the background's own
 * uid. Returns null when no such boundary exists: the last background, a single-background
 * page, or an unknown uid.
 */
export const getBoundaryIdForBackground = (backgroundUid: string, backgroundsInOrder: ItemObject[]): TransitionBoundaryId | null => {
  const backgroundIndex = backgroundsInOrder.findIndex((background) => background.uid === backgroundUid);
  if (backgroundIndex < 0 || backgroundIndex === backgroundsInOrder.length - 1) {
    return null;
  }
  return backgroundUid;
};

/**
 * Redux-mirror twin of PageTransitions.getTransitionOutForItem: resolves the transition on a
 * boundary from serialized transitions data (the `getCurrentPageTransitions` selector result).
 * Null when the collection is absent or the boundary carries no transition.
 */
export const getTransitionForBoundaryFromMirror = (transitions: PageTransitionsObject | undefined, boundaryId: TransitionBoundaryId): ItemTransitionObject | null => {
  if (!transitions) {
    return null;
  }
  return Object.values(transitions.transitionsHashMap).find((transition) => transition.fromItemUid === boundaryId) ?? null;
};

export const getItemTransitionTypeLabel = (type: ItemTransitionType | null): string => {
  switch (type) {
    case null:
      return window.i18next.t('pmwjs_none');
    case ItemTransitionType.FADE:
      return window.i18next.t('pmwjs_fade');
    case ItemTransitionType.SLIDE:
      return window.i18next.t('pmwjs_slide');
    case ItemTransitionType.WIPE:
      return window.i18next.t('pmwjs_wipe');
    case ItemTransitionType.DIP_TO_COLOR:
      return window.i18next.t('pmwjs_dip_to_color');
    default:
      return assertNever(type);
  }
};

/**
 * The largest EFFECTIVE duration a boundary supports: the render window clamps to the
 * shorter adjacent span, so any longer stored duration plays identically. Duration controls
 * (slider, stepper, region drag-resize) cap here — quantized down to the 0.1s step so the
 * cap itself is reachable.
 */
export const getMaxTransitionDurationForSpans = (fromSpanLength: number, toSpanLength: number): number => {
  const effectiveMax = Math.min(MAX_TRANSITION_DURATION, fromSpanLength, toSpanLength);
  return Math.max(MIN_TRANSITION_DURATION, Math.floor(effectiveMax * 10) / 10);
};

/** Whether this transition type carries a `direction` (slide-family motion). */
export const doesItemTransitionTypeHaveDirection = (type: ItemTransitionType): boolean => {
  return type === ItemTransitionType.SLIDE || type === ItemTransitionType.WIPE;
};

/**
 * Whether the background owns a boundary on the LIVE page (it has a timeline successor) —
 * the live-model twin of getBoundaryIdForBackground, for surfaces that read the Page
 * directly (the timeline context menu).
 */
export const doesBackgroundOwnBoundary = (page: Page, backgroundUid: string): boolean => {
  const backgroundsInOrder = page.items.getBackgroundsSortedByStartTime();
  const backgroundIndex = backgroundsInOrder.findIndex((background) => background.uid === backgroundUid);
  return backgroundIndex >= 0 && backgroundIndex < backgroundsInOrder.length - 1;
};

/** Value equality of two transitions, ignoring identity/endpoints — the "would replacing one with the other change anything" test. */
const areTransitionValuesEqual = (a: ItemTransitionObject, b: ItemTransitionObject): boolean => {
  if (a.type !== b.type || a.duration !== b.duration) {
    return false;
  }
  if ('direction' in a && 'direction' in b && a.direction !== b.direction) {
    return false;
  }
  if ('color' in a && 'color' in b && !a.color.every((channel, index) => channel === b.color[index])) {
    return false;
  }
  return true;
};

/**
 * Whether "apply between all backgrounds" would actually change anything: some boundary is
 * missing the source transition, differs from it in value, or (source empty) still has one
 * to clear. The apply-to-all affordances hide when this is false — a guaranteed no-op button
 * is noise. Mirror-side twin of PageTransitions.applyBoundaryTransitionToAllBoundaries's
 * no-op guard.
 */
export const wouldApplyToAllBoundariesChangeTransitions = (
  transitions: PageTransitionsObject | undefined,
  backgroundsInOrder: ItemObject[],
  sourceBoundaryId: TransitionBoundaryId
): boolean => {
  const transitionEntries = transitions ? Object.values(transitions.transitionsHashMap) : [];
  const sourceTransition = transitionEntries.find((entry) => entry.fromItemUid === sourceBoundaryId) ?? null;
  const boundaryOwners = backgroundsInOrder.slice(0, -1);
  if (!sourceTransition) {
    return transitionEntries.length > 0;
  }
  return boundaryOwners.some((owner) => {
    const existing = transitionEntries.find((entry) => entry.fromItemUid === owner.uid);
    return !existing || !areTransitionValuesEqual(existing, sourceTransition);
  });
};

/**
 * Plays the boundary's transition window (with lead-in/out padding) so an edit is immediately
 * visible on canvas. Shared by every transition-editing surface: the editor's type/duration/
 * direction controls and both color-picker hosts. No-op when the boundary has no transition
 * or its window can't render (mid-mutation adjacency gap).
 */
export const previewTransitionForBoundary = (page: Page, boundaryId: TransitionBoundaryId): void => {
  const transition = page.transitions.getTransitionOutForItem(boundaryId);
  if (!transition) {
    return;
  }
  const transitionWindow = page.transitions.getWindowForTransition(transition);
  if (!transitionWindow) {
    return;
  }
  void page.playBetweenStartAndEndTime(
    Math.max(0, transitionWindow.startTime - TRANSITION_PREVIEW_PADDING_SECONDS),
    Math.min(page.getDuration(), transitionWindow.endTime + TRANSITION_PREVIEW_PADDING_SECONDS)
  );
};
